/**
 * Prompt Management Page
 * Manage AI prompts for document processing
 */

import React, { useState, useMemo } from 'react';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Eye,
  Wand2,
  Brain,
  FileSearch,
  CheckCircle,
  Sparkles,
  Settings,
  Copy,
  Power,
  PowerOff,
} from 'lucide-react';
import { useBEStudy } from '@/hooks/useBEStudy';
import { DataTable } from '@/components/be-study/common/DataTable';
import { DeleteConfirmationModal } from '@/components/be-study/modals/ConfirmationModal';
import { AI_MODELS, formatDate } from '@/constants/beStudyMockData';
import { cn } from '@/lib/utils';

/**
 * Purpose icons mapping
 */
const PURPOSE_CONFIG = {
  TEMPLATE_PARSING: { icon: FileSearch, label: 'Template Parsing', color: 'purple' },
  DOCUMENT_GENERATION: { icon: Wand2, label: 'Content Generation', color: 'blue' },
  CONFIDENCE_ASSESSMENT: { icon: CheckCircle, label: 'Confidence Scoring', color: 'green' },
  SUMMARY_GENERATION: { icon: Sparkles, label: 'Summary Generation', color: 'amber' },
  COMPLIANCE_VERIFICATION: { icon: Brain, label: 'Compliance Check', color: 'red' },
};

/**
 * Prompt Detail Modal
 */
function PromptDetailModal({ prompt, open, onOpenChange }) {
  if (!prompt) return null;

  const purposeConfig = PURPOSE_CONFIG[prompt.purpose] || PURPOSE_CONFIG.DOCUMENT_GENERATION;
  const Icon = purposeConfig.icon;
  const model = AI_MODELS.find(m => m.id === prompt.model);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="prompt-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-lg',
              `bg-${purposeConfig.color}-100 dark:bg-${purposeConfig.color}-900/30`
            )}>
              <Icon className={cn('h-5 w-5', `text-${purposeConfig.color}-600`)} />
            </div>
            <div>
              <DialogTitle>{prompt.name}</DialogTitle>
              <DialogDescription>{purposeConfig.label}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Description */}
          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            <p className="text-sm mt-1">{prompt.description}</p>
          </div>

          {/* Model and Parameters */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Model</Label>
              <p className="text-sm mt-1 font-medium">{model?.name || prompt.model}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Temperature</Label>
              <p className="text-sm mt-1">{prompt.temperature}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Max Tokens</Label>
              <p className="text-sm mt-1">{prompt.maxTokens?.toLocaleString()}</p>
            </div>
          </div>

          {/* Status */}
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <div className="mt-1">
              <Badge variant={prompt.isActive ? 'default' : 'secondary'}>
                {prompt.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>

          {/* Prompt Text */}
          <div>
            <Label className="text-xs text-muted-foreground">Prompt</Label>
            <div className="mt-1 p-3 rounded-lg bg-muted text-sm font-mono whitespace-pre-wrap max-h-[200px] overflow-y-auto">
              {prompt.prompt}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Created</Label>
              <p className="text-sm mt-1">{formatDate(prompt.createdAt)}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Last Updated</Label>
              <p className="text-sm mt-1">{formatDate(prompt.updatedAt)}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit Prompt Modal
 */
function EditPromptModal({ prompt, open, onOpenChange, onSave, isNew = false }) {
  const [formData, setFormData] = useState({
    name: prompt?.name || '',
    description: prompt?.description || '',
    purpose: prompt?.purpose || 'DOCUMENT_GENERATION',
    model: prompt?.model || 'gpt-4',
    prompt: prompt?.prompt || '',
    temperature: prompt?.temperature || 0.5,
    maxTokens: prompt?.maxTokens || 4000,
  });

  React.useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name,
        description: prompt.description || '',
        purpose: prompt.purpose,
        model: prompt.model,
        prompt: prompt.prompt,
        temperature: prompt.temperature,
        maxTokens: prompt.maxTokens,
      });
    }
  }, [prompt]);

  const handleSave = () => {
    onSave?.(formData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="edit-prompt-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create New Prompt' : 'Edit Prompt'}</DialogTitle>
          <DialogDescription>
            {isNew ? 'Define a new AI prompt for document processing' : 'Modify prompt configuration'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="prompt-name">Prompt Name</Label>
            <Input
              id="prompt-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Content Generation"
              data-testid="prompt-name-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="prompt-description">Description</Label>
            <Input
              id="prompt-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this prompt's purpose"
              data-testid="prompt-description-input"
            />
          </div>

          {/* Purpose and Model */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Select
                value={formData.purpose}
                onValueChange={(value) => setFormData(prev => ({ ...prev, purpose: value }))}
              >
                <SelectTrigger data-testid="prompt-purpose-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PURPOSE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>AI Model</Label>
              <Select
                value={formData.model}
                onValueChange={(value) => setFormData(prev => ({ ...prev, model: value }))}
              >
                <SelectTrigger data-testid="prompt-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Temperature */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Temperature</Label>
              <span className="text-sm text-muted-foreground">{formData.temperature}</span>
            </div>
            <Slider
              value={[formData.temperature]}
              onValueChange={([value]) => setFormData(prev => ({ ...prev, temperature: value }))}
              min={0}
              max={1}
              step={0.1}
              data-testid="prompt-temperature-slider"
            />
            <p className="text-xs text-muted-foreground">
              Lower = more focused, Higher = more creative
            </p>
          </div>

          {/* Max Tokens */}
          <div className="space-y-2">
            <Label htmlFor="max-tokens">Max Tokens</Label>
            <Input
              id="max-tokens"
              type="number"
              value={formData.maxTokens}
              onChange={(e) => setFormData(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 4000 }))}
              min={100}
              max={16000}
              data-testid="prompt-max-tokens-input"
            />
          </div>

          {/* Prompt Text */}
          <div className="space-y-2">
            <Label htmlFor="prompt-text">Prompt Template</Label>
            <Textarea
              id="prompt-text"
              value={formData.prompt}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
              placeholder="Enter the AI prompt template..."
              rows={6}
              className="font-mono text-sm"
              data-testid="prompt-text-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="save-prompt-btn">
            {isNew ? 'Create Prompt' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Prompt Management Page Component
 */
export function PromptManagement() {
  const {
    prompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    togglePromptStatus,
    promptStatistics,
  } = useBEStudy();

  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Filter prompts
  const filteredPrompts = useMemo(() => {
    if (!searchQuery) return prompts;
    
    const query = searchQuery.toLowerCase();
    return prompts.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.description.toLowerCase().includes(query) ||
      p.purpose.toLowerCase().includes(query)
    );
  }, [prompts, searchQuery]);

  // Table columns
  const columns = [
    {
      accessorKey: 'name',
      header: 'Prompt Name',
      cell: ({ row }) => {
        const config = PURPOSE_CONFIG[row.purpose] || PURPOSE_CONFIG.DOCUMENT_GENERATION;
        const Icon = config.icon;
        
        return (
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-lg',
              `bg-${config.color}-100 dark:bg-${config.color}-900/30`
            )}>
              <Icon className={cn('h-4 w-4', `text-${config.color}-600`)} />
            </div>
            <div>
              <p className="font-medium">{row.name}</p>
              <p className="text-xs text-muted-foreground">{config.label}</p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'model',
      header: 'Model',
      cell: ({ value }) => {
        const model = AI_MODELS.find(m => m.id === value);
        return (
          <Badge variant="outline">
            {model?.name || value}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'temperature',
      header: 'Temperature',
      cell: ({ value }) => (
        <span className="text-sm font-mono">{value}</span>
      ),
    },
    {
      accessorKey: 'maxTokens',
      header: 'Max Tokens',
      cell: ({ value }) => (
        <span className="text-sm">{value?.toLocaleString()}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ value }) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  // Handlers
  const handleView = (prompt) => {
    setSelectedPrompt(prompt);
    setShowDetailModal(true);
  };

  const handleEdit = (prompt) => {
    setSelectedPrompt(prompt);
    setShowEditModal(true);
  };

  const handleDelete = (prompt) => {
    setSelectedPrompt(prompt);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = (prompt) => {
    togglePromptStatus(prompt.id);
  };

  const handleConfirmDelete = () => {
    if (selectedPrompt) {
      deletePrompt(selectedPrompt.id);
      setShowDeleteModal(false);
      setSelectedPrompt(null);
    }
  };

  const handleCreatePrompt = (data) => {
    createPrompt(data);
    setShowAddModal(false);
  };

  const handleUpdatePrompt = (data) => {
    if (selectedPrompt) {
      updatePrompt(selectedPrompt.id, data);
      setSelectedPrompt(null);
    }
  };

  // Row actions
  const rowActions = (row) => (
    <>
      <DropdownMenuItem onClick={() => handleView(row)} data-testid={`view-prompt-${row.id}`}>
        <Eye className="h-4 w-4 mr-2" />
        View Details
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleEdit(row)} data-testid={`edit-prompt-${row.id}`}>
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Copy className="h-4 w-4 mr-2" />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => handleToggleStatus(row)}>
        {row.isActive ? (
          <>
            <PowerOff className="h-4 w-4 mr-2" />
            Deactivate
          </>
        ) : (
          <>
            <Power className="h-4 w-4 mr-2" />
            Activate
          </>
        )}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem 
        onClick={() => handleDelete(row)}
        className="text-red-600"
        data-testid={`delete-prompt-${row.id}`}
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </>
  );

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="prompt-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Prompt Management</h1>
            <p className="text-muted-foreground">
              Configure AI prompts for document processing
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-prompt-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Prompt
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Prompts</p>
                  <p className="text-2xl font-bold">{promptStatistics.total}</p>
                </div>
                <div className="p-3 rounded-lg bg-primary/10">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold">{promptStatistics.active}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inactive</p>
                  <p className="text-2xl font-bold">{promptStatistics.inactive}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800">
                  <PowerOff className="h-5 w-5 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-prompts-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Prompts Table */}
        <Card>
          <CardContent className="p-0">
            <DataTable
              data={filteredPrompts}
              columns={columns}
              rowActions={rowActions}
              emptyMessage="No prompts found"
              onRowClick={handleView}
            />
          </CardContent>
        </Card>

        {/* Modals */}
        <EditPromptModal
          prompt={null}
          open={showAddModal}
          onOpenChange={setShowAddModal}
          onSave={handleCreatePrompt}
          isNew
        />

        <PromptDetailModal
          prompt={selectedPrompt}
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
        />

        <EditPromptModal
          prompt={selectedPrompt}
          open={showEditModal}
          onOpenChange={setShowEditModal}
          onSave={handleUpdatePrompt}
        />

        <DeleteConfirmationModal
          open={showDeleteModal}
          onOpenChange={setShowDeleteModal}
          onConfirm={handleConfirmDelete}
          itemName={selectedPrompt?.name}
          itemType="prompt"
        />
      </div>
    </BaseLayout>
  );
}

export default PromptManagement;
